import { Icon } from "armstrong-react";
import classNames from "classnames";
import React from "react";
import { Motion, OpaqueConfig, spring, Style } from "react-motion";
import _ from "underscore";

import SvgIcon, { DEFAULT_ICON_SVG } from "../../../core/atoms/svgIcon";
import { ContentTypeEnumHelper, MonthEnumHelper } from "@travellocal/utils/enumHelper";
import { MotionStyle, springConfig } from "../../../shared/helpers/motionHelper";

interface LocationFilterProps {
  regions: IRegionSummary[];
  countries: ICountrySummary[];
  selectedCountries: ICountrySummary[];
  onSelectionToggle: (newSelection: ICountrySummary[]) => void;
}

interface LocationFilterState {
  currentRegion: IRegionSummary | null;
}

export default class extends React.Component<LocationFilterProps, LocationFilterState> {
  constructor(props) {
    super(props, location => location.id);
    this.state = {
      currentRegion: null,
    };
  }

  private handleRegionToggle = (region: IRegionSummary) => {
    const isSelected = this.state.currentRegion && this.state.currentRegion.id === region.id;

    this.setState({
      currentRegion: isSelected ? null : region,
    });
  }

  private handleCountryToggle = (country: ICountrySummary) => {
    const selection = this.props.selectedCountries || [];
    const index = _.indexOf(selection.map(x => x.id), country.id);
    if (index >= 0) {
      selection.splice(index, 1);
    }
    else {
      selection.push(country);
    }

    this.props.onSelectionToggle(selection);
  }

  private selectAll = (all: ICountrySummary[]) => {
    const selection = this.props.selectedCountries || [];
    const newSelection = _.unique(selection.concat(all), x => x.id);
    this.props.onSelectionToggle(newSelection);
  }

  private clearAll = (all: ICountrySummary[]) => {
    const selection = this.props.selectedCountries || [];
    const newSelection = _.without(selection, ...all);
    this.props.onSelectionToggle(newSelection);
  }

  private renderCountries = () => {
    const regionCountries = this.props.countries.filter(x => x.parent.id === this.state.currentRegion.id);
    const isAllSelected = regionCountries.every(x => this.props.selectedCountries.indexOf(x) > -1);

    return (
      <div className="countries">
        <div className="cancel" onClick={() => this.setState({ currentRegion: null })}>
          <Icon icon={Icon.Icomoon.arrowLeft} />
        </div>
        <div className="toggles">
          <header>
            <h3>{this.state.currentRegion.name}</h3>
            <p>Choose countries to see inspiration...</p>
          </header>

          {regionCountries.length > 1 &&
            <div
              className={classNames("toggle", "select-all", { selected: isAllSelected })}
              onClick={e => isAllSelected ? this.clearAll(regionCountries) : this.selectAll(regionCountries)}
            >
              <label>All</label>
            </div>
          }

          {regionCountries.map(country => {
            const isSelected = this.props.selectedCountries.some(c => c.id === country.id);
            const imageUrl = country.heroImage ? country.heroImage.thumbnailUrl : "/images/missing-image.jpg";

            return (
              <div key={`filter-loc-country-toggle-${country.id}`}
                   className={classNames("toggle", { selected: isSelected })}
                   onClick={e => this.handleCountryToggle(country)}>
                <div className="background" style={{ backgroundImage: `url('${imageUrl}')` }} />
                <label>{country.name}</label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  public render() {
    const motionStyle: MotionStyle = {
      translateX: spring(this.state.currentRegion ? -100 : 0, springConfig),
    };

    return (
      <div className="filter location">
        <Motion style={motionStyle}>
          {(m: MotionStyle) => {
            const cssStyle = {
              transform: `translate3d(${m.translateX}%, 0px, 0px)`,
              WebkitTransform: `translate3d(${m.translateX}%, 0px, 0px)`,
            };

            return (
              <div className="location-picker" style={cssStyle}>
                <div className="regions toggles">
                  <header>
                    <p>Choose a region...</p>
                  </header>
                  {this.props.regions.map(region => {
                    const selectedCountryCount = (this.props.selectedCountries || []).filter(x => x.parent?.id === region.id).length;
                    const hasSelection = selectedCountryCount > 0;
                    const imageUrl = region.heroImage ? region.heroImage.thumbnailUrl : "/images/missing-image.jpg";

                    return (
                      <div key={`filter-loc-reg-toggle-${region.id}`}
                        className={classNames("toggle", { "has-selection": hasSelection })}
                        onClick={e => this.handleRegionToggle(region)}>
                        <div className="background" style={{ backgroundImage: `url('${imageUrl}')` }} />
                        <label>{region.name}</label>
                        {selectedCountryCount < 1 && <Icon icon={Icon.Icomoon.arrowRight} />}
                        {selectedCountryCount >= 1 && <div className="count-bubble secondary">{selectedCountryCount}</div>}
                      </div>
                    );
                  })}
                </div>
                {this.state.currentRegion &&
                  this.renderCountries()
                }
              </div>
            );
          }}
        </Motion>
      </div>
    );
  }
}
